import React from 'react';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import SvgIcon from '@mui/material/SvgIcon';
import Typography from '@mui/material/Typography';
import FormHelperText from '@mui/material/FormHelperText';

import { Button } from '../../atoms/button/button';
import { Input } from '../../atoms/input/input';
import { InputLabel } from '../../atoms/input-label/input-label';
import { FormControl } from '../../atoms/form-control/form-control';
import { PhoneInput } from '../../molecules/phone-input/phone-input';

import checkMarkIcon from '../../../../public/static/icons/checkmark-plain.svg';

import { StyledContactFormHeader, StyledRequestCallFormContainer } from './ship-contact-form.style';

import { __ } from '../../../utils/translation-service';
import { ContactDirectlyBlock } from './direct-contact-info';
import CircularProgress from '@mui/material/CircularProgress';
import { colorsUnifications } from '../../../style/global-style';

export interface IContactFormValues {
    firstName: string;
    lastName: string;
    emailAddress: string;
    phoneNumber: string;
    phoneCode: string;
    message: string;
}

interface IShipContactFormProps {
    isLoading?: boolean;
    formSubmitState: 'success' | 'error' | 'indefinite';
    countryCode: string;
    cruiseTitle: string;
    onSubmit: () => void;
    errors: Partial<Record<keyof IContactFormValues, string | undefined>>;
    onFieldChange: (fieldName: string, value: string) => void;
}

export const ShipContactForm = ({
    cruiseTitle,
    countryCode,
    errors,
    onSubmit,
    onFieldChange,
    formSubmitState,
    isLoading,
}: IShipContactFormProps) => {
    const hasValidationError = errors.firstName || errors.lastName || errors.phoneNumber;
    const hasSubmitError = formSubmitState === 'error';
    const showValidationError = hasValidationError && !hasSubmitError;
    const isRequestSent = formSubmitState === 'success';
    const showSendButton = !isLoading && !isRequestSent;

    return (
        <Container disableGutters>
            <Grid container>
                <Grid item xs={12} md={7}>
                    <StyledRequestCallFormContainer>
                        <StyledContactFormHeader>
                            {__('shipContactForm.header').replace('%ship%', cruiseTitle)}
                        </StyledContactFormHeader>

                        <Typography
                            variant="subtitle1"
                            sx={{
                                fontSize: '16px',
                                fontWeight: '600',
                                mb: '36px',
                            }}
                        >
                            {__('shipContactForm.subTitle')}
                        </Typography>

                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth error={Boolean(errors.firstName)}>
                                    <InputLabel>{__('general.firstName')}</InputLabel>
                                    <Input
                                        name="firstName"
                                        onBlur={(e) => onFieldChange('firstName', e.target.value)}
                                    />
                                    {errors.firstName && (
                                        <FormHelperText>{__(errors.firstName)}</FormHelperText>
                                    )}
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth error={Boolean(errors.lastName)}>
                                    <InputLabel>{__('general.lastName')}</InputLabel>
                                    <Input
                                        name="lastName"
                                        onBlur={(e) => onFieldChange('lastName', e.target.value)}
                                    />
                                    {errors.lastName && (
                                        <FormHelperText>{__(errors.lastName)}</FormHelperText>
                                    )}
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth error={Boolean(errors.emailAddress)}>
                                    <InputLabel>{__('general.email')}</InputLabel>
                                    <Input
                                        name="emailAddress"
                                        onBlur={(e) => onFieldChange('emailAddress', e.target.value)}
                                    />
                                    {errors.emailAddress && (
                                        <FormHelperText>{__(errors.emailAddress)}</FormHelperText>
                                    )}
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth error={Boolean(errors.phoneNumber)}>
                                    <InputLabel>{__('general.phoneNumber')}</InputLabel>
                                    <PhoneInput
                                        countryCode={countryCode}
                                        name="phoneNumber"
                                        onChange={(fullNumber, phoneData) => {
                                            onFieldChange('phoneCode', phoneData.code);
                                            onFieldChange('phoneNumber', phoneData.number);
                                        }}
                                    />

                                    {errors.phoneNumber && (
                                        <FormHelperText>{__(errors.phoneNumber)}</FormHelperText>
                                    )}
                                </FormControl>
                            </Grid>
                            <Grid item xs={12}>
                                <FormControl sx={{ mb: '36px' }} fullWidth>
                                    <InputLabel>{__('general.message')}</InputLabel>
                                    <Input
                                        multiline
                                        name="message"
                                        onBlur={(e) => onFieldChange('message', e.target.value)}
                                    />
                                </FormControl>
                            </Grid>
                        </Grid>

                        {(hasValidationError || hasSubmitError) && (
                            <FormHelperText
                                sx={{
                                    marginTop: 0,
                                    marginBottom: '36px',
                                    fontSize: '14px',
                                    color: colorsUnifications.watermelon[100],
                                }}
                            >
                                {hasSubmitError && __('general.somethingWentWrong')}
                                {showValidationError && __('general.pleaseFillAllRequiredFields')}
                            </FormHelperText>
                        )}

                        <Button
                            fullWidth
                            size="large"
                            variant={isRequestSent ? 'outlined' : 'contained'}
                            onClick={isRequestSent || isLoading ? undefined : onSubmit}
                        >
                            {isLoading && (
                                <CircularProgress size={24} color="inherit" sx={{ mr: '8px' }} />
                            )}

                            {isRequestSent && (
                                <SvgIcon
                                    inheritViewBox
                                    component={checkMarkIcon}
                                    sx={{ width: '12px', height: '12px', mr: '8px' }}
                                />
                            )}

                            {isRequestSent && __('general.thanksForYourRequest')}
                            {showSendButton && __('send')}
                        </Button>
                    </StyledRequestCallFormContainer>
                </Grid>
                <Grid item xs={12} md={5}>
                    <ContactDirectlyBlock />
                </Grid>
            </Grid>
        </Container>
    );
};
